import {
  computed,
  reactive,
  type ComputedRef,
  type WritableComputedRef,
} from "vue";
import { useMjmlStore } from "@/stores/mjmlStore";
import { wasAttrChanged } from "@/utils/spacingUtils";
import type { ResetableValue } from "../../types/Mjml/IMjmlStore";

interface RawAttrBinding<T> {
  model: WritableComputedRef<T>;
  changed: ComputedRef<boolean>;
  defaultValue: ComputedRef<T>;
  meta: ComputedRef<ResetableValue | null>;
  reset: () => void;
}

export type AttrBinding<T> = {
  model: T;
  changed: boolean;
  defaultValue: T;
  meta: ResetableValue | null;
  reset: () => void;
};

type BlockAttrOptions =
  | { mode: "global"; block: string }
  | { mode: "active" }
  | { mode: "section" }
  | { mode: "column"; columnIndex: number; blockColumn?: boolean }
  | { mode: "active-group"; group: string }
  | { mode: "global-group"; block: string; group: string };

export function useBlockAttr(options: BlockAttrOptions) {
  const mjmlStore = useMjmlStore();

  function getDefault(block: string, prop: string): any {
    const base = mjmlStore.getBaseAttrWithDefault(block, prop);
    return base?.value ?? base?.defaultValue;
  }

  function getMeta(prop: string): ResetableValue | null {
    if (options.mode === "global") {
      return mjmlStore.getBaseAttrWithDefault(options.block, prop);
    }
    if (options.mode === "global-group") {
      return mjmlStore.getBaseGroupAttrWithDefault(
        options.block,
        options.group,
        prop
      );
    }
    if (options.mode === "active-group") {
      return mjmlStore.getActiveBlockGroupAttrWithDefault(options.group, prop);
    }
    if (options.mode === "section") {
      const value = mjmlStore.getActiveSectionAttrString(prop);
      const defaultValue = getDefault("mj-section", prop);
      return {
        value,
        wasChanged: wasAttrChanged(value, defaultValue),
        defaultValue,
      };
    }
    if (options.mode === "column") {
      const section = options.blockColumn
        ? mjmlStore.getActiveBlock
        : mjmlStore.getSelectedSectionByIndex();
      const column = section?.children?.[options.columnIndex];
      const value = column?.attributes?.[prop];
      const defaultValue = getDefault("mj-column", prop);
      return {
        value,
        wasChanged: wasAttrChanged(value, defaultValue),
        defaultValue,
      };
    }
    return mjmlStore.getActiveBlockAttrWithDefault(prop);
  }

  function setValue(prop: string, value: any) {
    if (options.mode === "global") {
      mjmlStore.updateUnchangedBlockAttrGlobally({
        block: options.block,
        prop,
        value,
      });
    } else if (options.mode === "global-group") {
      mjmlStore.updateUnchangedBlockGroupAttrGlobally({
        block: options.block,
        group: options.group,
        prop,
        value,
      });
    } else if (options.mode === "active-group") {
      mjmlStore.updateBlockGroupAttribute({
        group: options.group,
        prop,
        value,
      });
    } else if (options.mode === "section") {
      mjmlStore.updateSectionAttribute({ prop, value });
    } else if (options.mode === "column") {
      const updateFn = options.blockColumn
        ? mjmlStore.updateSpecificBlockColumnAttribute
        : mjmlStore.updateSpecificColumnAttribute;
      updateFn({ columnIndex: options.columnIndex, prop, value });
    } else {
      mjmlStore.updateBlockAttribute({ prop, value });
    }
  }

  function resetAttr(prop: string) {
    const meta = getMeta(prop);
    setValue(prop, meta?.defaultValue);
  }

  function bind<T>(raw: RawAttrBinding<T>): AttrBinding<T> {
    return reactive(raw) as AttrBinding<T>;
  }

  function str(prop: string, fallback?: string): AttrBinding<string> {
    const meta = computed(() => getMeta(prop));

    return bind({
      model: computed({
        get() {
          return meta.value?.value ?? fallback ?? "";
        },
        set(value: string) {
          setValue(prop, value);
        },
      }),
      changed: computed(() => meta.value?.wasChanged ?? false),
      defaultValue: computed(() => meta.value?.defaultValue ?? fallback ?? ""),
      meta,
      reset: () => resetAttr(prop),
    });
  }

  function px(prop: string, fallback?: number): AttrBinding<number> {
    const meta = computed(() => getMeta(prop));

    return bind({
      model: computed({
        get() {
          return parseInt(meta.value?.value) || (fallback ?? 0);
        },
        set(value: number) {
          setValue(prop, value + "px");
        },
      }),
      changed: computed(() => meta.value?.wasChanged ?? false),
      defaultValue: computed(
        () => parseInt(meta.value?.defaultValue) || (fallback ?? 0)
      ),
      meta,
      reset: () => resetAttr(prop),
    });
  }

  function nullable(prop: string): AttrBinding<any> {
    const meta = computed(() => getMeta(prop));

    return bind({
      model: computed({
        get() {
          return meta.value?.value;
        },
        set(value: any) {
          setValue(prop, value);
        },
      }),
      changed: computed(() => meta.value?.wasChanged ?? false),
      defaultValue: computed(() => meta.value?.defaultValue),
      meta,
      reset: () => resetAttr(prop),
    });
  }

  function bool(
    prop: string,
    trueVal: string,
    falseVal: string
  ): AttrBinding<boolean> {
    const meta = computed(() => getMeta(prop));

    return bind({
      model: computed({
        get() {
          const raw = meta.value?.value;
          return raw === trueVal;
        },
        set(value: boolean) {
          setValue(prop, value ? trueVal : falseVal);
        },
      }),
      changed: computed(() => meta.value?.wasChanged ?? false),
      defaultValue: computed(() => meta.value?.defaultValue === trueVal),
      meta,
      reset: () => resetAttr(prop),
    });
  }

  function anyChanged(...bindings: AttrBinding<any>[]): ComputedRef<boolean> {
    return computed(() => bindings.some((b) => b.changed));
  }

  function anyPropChanged(...props: string[]): ComputedRef<boolean> {
    return computed(() => props.some((p) => getMeta(p)?.wasChanged));
  }

  return {
    str,
    px,
    nullable,
    bool,
    resetAttr,
    anyChanged,
    anyPropChanged,
  };
}
